import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileText, Trash2, LogOut, RefreshCw, CheckCircle2, Download, Replace,
  Image as ImageIcon, FolderPlus, Library, Pencil, X, Sliders, Users, Percent, Package, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore, getLocalized, useLanguageStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import LocalizedField from '../components/admin/LocalizedField';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';
import { useTranslation } from '../i18n/translations';

// --- СЛУЖЕБНЫЕ КОНСТАНТЫ И ХЕЛПЕРЫ ---
const EMPTY_LOC = { en: '', nl: '', ro: '', ru: '' };

// Вспомогательная функция для получения сегодняшней даты в формате YYYY-MM-DD
const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ИСПРАВЛЕНО: Возвращен утерянный хелпер проверки локализации, из-за которого падал рендеринг
const hasLoc = (v) => Boolean(v && (v.en || v.nl || v.ro || v.ru));

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const FORBIDDEN_EXT = new Set(['exe', 'sh', 'bat', 'cmd', 'msi', 'app']);

const ASSET_CATEGORIES = [
  { value: 'catalogue', label: 'Catalogue' },
  { value: '3d_model', label: '3D Model' },
  { value: 'tech_pack', label: 'Technical Pack' },
  { value: 'brochure', label: 'Brochure' },
  { value: 'specification', label: 'Specification' },
  { value: 'other', label: 'Other' },
];

const STAGE_LABELS = {
  received: '1. Принят в обработку',
  design: '2. Проектирование и КД',
  production: '3. В производстве',
  qc_packing: '4. ОТК и упаковка',
  ready: '5. Готов к монтажу',
  completed: '6. Выполнен / Отгружен' // Полная синхронизация с 6-ю этапами бэкенда
};

const STAGES_ORDER = ['received', 'design', 'production', 'qc_packing', 'ready', 'completed'];

const getStageStyle = (status) => {
  switch(status) {
    case 'received': return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    case 'design': return 'bg-blue-50 text-blue-700 border-blue-300';
    case 'production': return 'bg-amber-50 text-amber-700 border-amber-300';
    case 'qc_packing': return 'bg-purple-50 text-purple-700 border-purple-300';
    case 'ready': return 'bg-emerald-50 text-emerald-700 border-emerald-300';
    case 'completed': return 'bg-slate-900 text-white border-slate-950'; // Стиль для завершенного архивного этапа
    default: return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

export async function uploadToCloudinary(file, token, folder, resourceType = 'raw') {
  const sigRes = await fetch(`${API}/api/cloudinary/signature?folder=${folder}&resource_type=${resourceType}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!sigRes.ok) throw new Error('Signature failed');
  const sig = await sigRes.json();
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  const endpoint = resourceType === 'image' ? 'image' : 'raw';
  const upRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/${endpoint}/upload`, {
    method: 'POST', body: form,
  });
  if (!upRes.ok) throw new Error('Upload failed');
  return await upRes.json();
}

export const uploadRawToCloudinary = (file, token, folder) => uploadToCloudinary(file, token, folder, 'raw');

export function validateUploadFile(file) {
  if (file.size > MAX_FILE_SIZE) return `${file.name} exceeds 100 MB`;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (FORBIDDEN_EXT.has(ext)) return `File type .${ext} is not allowed`;
  return null;
}

// ============================================================
// Catalogue section (PDF + cover image)
// ============================================================
const CatalogueSection = ({ token }) => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [meta, setMeta] = useState({ title: { ...EMPTY_LOC }, subtitle: { ...EMPTY_LOC }, description: { ...EMPTY_LOC } });
  
  const [catalogLang, setCatalogLang] = useState('en');
  
  const pdfRef = useRef(null);
  const coverRef = useRef(null);

  const applyCatalogue = (data) => {
    setCatalogue(data);
    if (data) {
      setMeta({
        title: { ...EMPTY_LOC, ...(data.title || {}) },
        subtitle: { ...EMPTY_LOC, ...(data.subtitle || {}) },
        description: { ...EMPTY_LOC, ...(data.description || {}) },
      });
    } else {
      setMeta({ title: { ...EMPTY_LOC }, subtitle: { ...EMPTY_LOC }, description: { ...EMPTY_LOC } });
    }
  };

  const fetchCatalogue = async (targetLang = catalogLang) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/site-settings/catalogue?lang=${targetLang}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      applyCatalogue(data.catalogue);
    } catch (err) {
      console.error(err);
    } finally { // ИСПРАВЛЕНО: Заменено ошибочное ключевое слово "gold" на валидный "finally"
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalogue(catalogLang);
  }, [catalogLang, token]);

  const cancelMeta = () => {
    setEditingMeta(false);
    fetchCatalogue(catalogLang);
  };

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      const res = await fetch(`${API}/api/admin/site-settings/catalogue/meta?lang=${catalogLang}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
          title: meta.title, 
          subtitle: meta.subtitle, 
          description: meta.description,
          language: catalogLang 
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Collection Book text and language saved');
      setEditingMeta(false);
      fetchCatalogue(catalogLang);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleUploadPdf = async (file) => {
    if (!file) return;
    const err = validateUploadFile(file);
    if (err) { toast.error(err); return; }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext !== 'pdf') { toast.error('Collection Book must be a PDF'); return; }
    setUploadingPdf(true);
    try {
      const up = await uploadToCloudinary(file, token, 'site', 'raw');
      const res = await fetch(`${API}/api/admin/site-settings/catalogue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          public_id: up.public_id, secure_url: up.secure_url,
          language: catalogLang,
          filename: file.name,
          title: meta.title, subtitle: meta.subtitle, description: meta.description,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success(`Catalogue PDF published for version: ${catalogLang.toUpperCase()}`);
      fetchCatalogue(catalogLang);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploadingPdf(false);
      if (pdfRef.current) pdfRef.current.value = '';
    }
  };

  const handleUploadCover = async (file) => {
    if (!file) return;
    const err = validateUploadFile(file);
    if (err) { toast.error(err); return; }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      toast.error('Cover image must be JPG, PNG or WebP'); return;
    }
    setUploadingCover(true);
    try {
      const up = await uploadToCloudinary(file, token, 'site', 'image');
      const res = await fetch(`${API}/api/admin/site-settings/catalogue/cover?lang=${catalogLang}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cover_image_url: up.secure_url, cover_image_public_id: up.public_id }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Cover image updated');
      fetchCatalogue(catalogLang);
    } catch (e) {
      toast.error(e.message || 'Cover upload failed');
    } finally {
      setUploadingCover(false);
      if (coverRef.current) coverRef.current.value = '';
    }
  };

  const handleDeleteCover = async () => {
    if (!window.confirm('Remove the catalogue cover image?')) return;
    try {
      const res = await fetch(`${API}/api/admin/site-settings/catalogue/cover?lang=${catalogLang}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Cover removed'); fetchCatalogue(catalogLang);
    } catch { toast.error('Failed to remove cover'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove the entire Collection Book PDF for ${catalogLang.toUpperCase()}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/site-settings/catalogue?lang=${catalogLang}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Catalogue removed'); fetchCatalogue(catalogLang);
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <section className="border border-border p-8 mb-12 bg-white text-left" data-testid="catalogue-section">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <p className="text-caption text-muted-foreground mb-2 flex items-center gap-2 text-xs">
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> B2B Trade
          </p>
          <h2 className="text-xl font-medium text-gray-900">Collection Book</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Global brand catalogue (PDF) + cover image. Powers `/b2b` and `/b2b/downloads`.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchCatalogue(catalogLang)} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh
        </Button>
      </div>

      <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => handleUploadPdf(e.target.files?.[0])} data-testid="catalogue-file-input" />
      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadCover(e.target.files?.[0])} data-testid="catalogue-cover-input" />

      {loading ? (
        <div className="py-12 text-center text-xs text-gray-400">{t?.common?.loading || 'Loading…'}</div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-4">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3 flex items-center gap-1.5 font-semibold text-gray-400">
              <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} />Cover Image
            </p>
            {catalogue?.cover_image_url ? (
              <div className="space-y-3" data-testid="catalogue-cover-published">
                <img src={catalogue.cover_image_url} alt="cover" className="w-full aspect-[3/4] object-cover border border-gray-100 rounded" />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 text-xs bg-white text-gray-900" onClick={() => coverRef.current?.click()} disabled={uploadingCover} data-testid="catalogue-cover-replace">
                    <Replace className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Replace
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeleteCover} className="text-destructive bg-white" data-testid="catalogue-cover-delete">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => coverRef.current?.click()}
                disabled={uploadingCover}
                className="w-full aspect-[3/4] border border-dashed border-gray-200 rounded flex flex-col items-center justify-center text-gray-400 hover:border-black hover:text-black transition-colors bg-gray-50"
                data-testid="catalogue-cover-empty"
              >
                <ImageIcon className="w-6 h-6 mb-2" strokeWidth={1.5} />
                <p className="text-xs">Add cover image</p>
                <p className="text-[10px] text-gray-400 mt-0.5">JPG / PNG / WebP</p>
              </button>
            )}
          </div>

          <div className="md:col-span-8">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3 flex items-center gap-1.5 font-semibold text-gray-400">
              <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />Catalogue PDF
            </p>

            <div className="mb-4 max-w-xs">
              <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5">Catalogue Language *</label>
              <select
                value={catalogLang} onChange={(e) => setCatalogLang(e.target.value)}
                className="w-full h-9 px-2.5 border border-gray-200 text-xs rounded appearance-none cursor-pointer font-medium bg-white focus:outline-none focus:border-black"
                style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='1.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center' }}
              >
                <option value="en">English (EN)</option>
                <option value="ro">Romanian (RO)</option>
                <option value="nl">Dutch (NL)</option>
                <option value="ru">Russian (RU)</option>
              </select>
            </div>

            {catalogue?.secure_url ? (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="border border-gray-100 p-5 bg-gray-50 flex items-center gap-4 rounded"
                data-testid="catalogue-published-row">
                <div className="w-12 h-16 bg-black text-white flex items-center justify-center flex-shrink-0 rounded">
                  <FileText className="w-6 h-6" strokeWidth={1.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-caption text-emerald-600 mb-0.5 flex items-center gap-1 text-[11px] font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Published
                  </p>
                  <p className="font-medium text-xs truncate text-gray-800 flex items-center gap-2">
                    {catalogue.filename || catalogue.title}
                    <span className="px-1.5 py-0.5 bg-neutral-900 text-white font-mono uppercase text-[8.5px] tracking-wider font-semibold rounded-sm">
                      {catalogue.language || catalogLang}
                    </span>
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Updated {new Date(catalogue.updated_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <a href={catalogue.secure_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center text-[10px] font-medium tracking-wider uppercase text-gray-500 hover:text-black border-b border-transparent hover:border-black pb-0.5"
                    data-testid="catalogue-preview-link">
                    <Download className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />Preview
                  </a>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="text-xs bg-white text-gray-900" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} data-testid="catalogue-replace-btn">
                      <Replace className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />Replace
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDelete} className="text-destructive bg-white" data-testid="catalogue-delete-btn">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="border border-dashed border-gray-200 p-10 text-center rounded bg-gray-50" data-testid="catalogue-empty-state">
                <Upload className="w-8 h-8 mx-auto mb-3 text-gray-400" strokeWidth={1.5} />
                <p className="text-xs text-gray-400 mb-3">No catalogue published yet for {catalogLang.toUpperCase()}.</p>
                <button 
                  type="button"
                  onClick={() => pdfRef.current?.click()} 
                  disabled={uploadingPdf} 
                  className="inline-flex items-center justify-center bg-black hover:bg-gray-800 text-white font-medium text-xs h-9 px-4 transition-colors rounded-none" 
                  data-testid="catalogue-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                  {uploadingPdf ? 'Uploading…' : 'Upload PDF'}
                </button>
                <p className="text-[10px] text-gray-400 mt-2">PDF only · max 100 MB</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 border-t border-gray-100 pt-6" data-testid="catalogue-meta">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-1.5 font-semibold text-gray-400">
              <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />Catalogue Translations ({catalogLang.toUpperCase()})
            </p>
            {editingMeta ? (
              <div className="flex items-center gap-2">
                <TranslateAllButton
                  testid="catalogue-translate-all"
                  fields={() => [
                    { value: meta.title, onChange: (v) => setMeta((p) => ({ ...p, title: v })) },
                    { value: meta.subtitle, onChange: (v) => setMeta((p) => ({ ...p, subtitle: v })) },
                    { value: meta.description, onChange: (v) => setMeta((p) => ({ ...p, description: v })) },
                  ]}
                />
                <button 
                  type="button"
                  onClick={handleSaveMeta} 
                  disabled={savingMeta} 
                  className="inline-flex items-center justify-center bg-black text-white text-xs px-4 h-9 font-medium transition-colors rounded-none" 
                  data-testid="catalogue-meta-save"
                >
                  {savingMeta ? 'Saving…' : 'Save Text'}
                </button>
                <Button variant="outline" size="sm" className="text-xs bg-white text-gray-900 h-9 rounded-none" onClick={cancelMeta} data-testid="catalogue-meta-cancel">
                  <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{t?.common?.cancel || 'Cancel'}
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" className="text-xs bg-white text-gray-900" onClick={() => setEditingMeta(true)} data-testid="catalogue-meta-edit">
                <Pencil className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Edit Text
              </Button>
            )}
          </div>

          {editingMeta ? (
            <div className="space-y-4 max-w-2xl">
              <LocalizedField label="Title" testid="catalogue-meta-title" value={meta.title} onChange={(v) => setMeta((p) => ({ ...p, title: v }))} />
              <LocalizedField label="Subtitle" testid="catalogue-meta-subtitle" value={meta.subtitle} onChange={(v) => setMeta((p) => ({ ...p, subtitle: v }))} />
              <LocalizedField label="Description" testid="catalogue-meta-description" multiline rows={2} value={meta.description} onChange={(v) => setMeta((p) => ({ ...p, description: v }))} />
            </div>
          ) : (
            <div className="space-y-1 p-4 bg-gray-50 border border-gray-100 rounded" data-testid="catalogue-meta-display">
              <p className="text-xs font-semibold text-gray-800">{getLocalized(meta.title, 'en') || 'Collection Book 2026'}</p>
              {hasLoc(meta.subtitle) && <p className="text-xs text-gray-500">{getLocalized(meta.subtitle, 'en')}</p>}
              {hasLoc(meta.description) && <p className="text-xs text-gray-400 max-w-xl">{getLocalized(meta.description, 'en')}</p>}
            </div>
          )}
        </div>
        </>
      )}
    </section>
  );
};

// ============================================================
// Trade Library (B2B Assets) section
// ============================================================
const TradeLibrarySection = ({ token }) => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: { ...EMPTY_LOC }, category: 'catalogue', language: 'en', sub_tag: '', description: { ...EMPTY_LOC } });
  const [cover, setCover] = useState(null);
  const [assetFile, setAssetFile] = useState(null);
  const fileRef = useRef(null);
  const coverRef = useRef(null);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/b2b/assets`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAssets(data.assets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/b2b/assets`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (alive) { setAssets(data.assets || []); setLoading(false); }
      } catch (err) {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const resetForm = () => {
    setForm({ title: { ...EMPTY_LOC }, category: 'catalogue', language: 'en', sub_tag: '', description: { ...EMPTY_LOC } });
    setCover(null);
    setAssetFile(null);
    setEditingId(null);
  };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (a) => {
    setForm({
      title: { ...EMPTY_LOC, ...(a.title || {}) },
      category: a.category || 'catalogue',
      language: a.language || 'en',
      sub_tag: a.sub_tag || '',
      description: { ...EMPTY_LOC, ...(a.description || {}) },
    });
    setCover(a.image_url ? { image_url: a.image_url, image_public_id: a.image_public_id } : null);
    setAssetFile({ file_url: a.file_url, file_public_id: a.file_public_id, filename: a.filename, file_size: a.file_size, mime_type: a.mime_type });
    setEditingId(a.id);
    setShowForm(true);
  };

  const cancelForm = () => { resetForm(); setShowForm(false); };

  const handleUploadCover = async (file) => {
    if (!file) return;
    const err = validateUploadFile(file);
    if (err) { toast.error(err); return; }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) { toast.error('Cover image must be JPG, PNG or WebP'); return; }
    setUploadingCover(true);
    try {
      const up = await uploadToCloudinary(file, token, 'b2b-assets', 'image');
      setCover({ image_url: up.secure_url, image_public_id: up.public_id });
      toast.success('Cover image ready');
    } catch (e) {
      toast.error(e.message || 'Cover upload failed');
    } finally {
      setUploadingCover(false);
      if (coverRef.current) coverRef.current.value = '';
    }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    const err = validateUploadFile(file);
    if (err) { toast.error(err); return; }
    setUploadingFile(true);
    try {
      const up = await uploadToCloudinary(file, token, 'b2b-assets', 'raw');
      setAssetFile({ file_url: up.secure_url, file_public_id: up.public_id, filename: file.name, file_size: file.size, mime_type: file.type || null });
      toast.success('File ready');
    } catch (e) {
      toast.error(e.message || 'File upload failed');
    } finally {
      setUploadingFile(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!(form.title.en || '').trim()) { toast.error('English title is required'); return; }
    if (!assetFile) { toast.error('Please choose an asset file'); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        language: form.language,
        sub_tag: form.sub_tag.trim() || null,
        description: hasLoc(form.description) ? form.description : null,
        image_url: cover?.image_url || null,
        image_public_id: cover?.image_public_id || null,
        file_url: assetFile.file_url,
        file_public_id: assetFile.file_public_id,
        filename: assetFile.filename,
        file_size: assetFile.file_size,
        mime_type: assetFile.mime_type,
      };
      const url = editingId ? `${API}/api/admin/b2b/assets/${editingId}` : `${API}/api/admin/b2b/assets`;
      const res = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Save failed');
      }
      toast.success(editingId ? 'Asset updated' : `"${form.title.en}" published`);
      cancelForm();
      fetchAssets();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/b2b/assets/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Asset deleted'); fetchAssets();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <section className="border border-border p-8 bg-white rounded-lg shadow-sm text-left" data-testid="trade-library-section">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <p className="text-caption text-muted-foreground mb-2 flex items-center gap-2 text-xs">
            <Library className="w-3.5 h-3.5" strokeWidth={1.5} /> B2B Trade
          </p>
          <h2 className="text-xl font-medium text-gray-900">Trade Library</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Extra catalogues, 3D models, tech packs and brochures. Visible to approved B2B partners on `/b2b/downloads`.
          </p>
        </div>
        <Button variant="outline" size="sm" className="text-xs bg-white text-gray-900" onClick={() => { if (showForm) cancelForm(); else openCreate(); }} data-testid="trade-library-add-btn">
          <FolderPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
          {showForm ? (t?.common?.cancel || 'Cancel') : 'Add Asset'}
        </Button>
      </div>

      <input ref={fileRef} type="file" className="hidden" onChange={(e) => handleUploadFile(e.target.files?.[0])} data-testid="trade-library-file-input" />
      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadCover(e.target.files?.[0])} data-testid="trade-library-cover-input" />

      {showForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
          className="border border-gray-100 p-5 mb-6 bg-gray-50 rounded" data-testid="trade-library-form">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <p className="text-[10px] font-semibold tracking-wider uppercase text-gray-400">
              {editingId ? 'Edit Asset' : 'New Asset'}
            </p>
            <TranslateAllButton
              testid="trade-translate-all"
              fields={() => [
                { value: form.title, onChange: (v) => setForm((p) => ({ ...p, title: v })) },
                { value: form.description, onChange: (v) => setForm((p) => ({ ...p, description: v })) },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5 font-semibold text-gray-400">
                <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} />Cover Image (optional)
              </p>
              {cover?.image_url ? (
                <div className="space-y-3" data-testid="trade-library-cover-preview">
                  <img src={cover.image_url} alt="cover" className="w-full aspect-[4/3] object-cover border border-brand rounded" />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs bg-white text-gray-900" onClick={() => coverRef.current?.click()} disabled={uploadingCover} data-testid="trade-library-cover-replace">
                      <Replace className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Replace
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCover(null)} className="text-destructive bg-white" data-testid="trade-library-cover-remove">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => coverRef.current?.click()}
                  disabled={uploadingCover}
                  className="w-full aspect-[4/3] border border-dashed border-gray-200 bg-white rounded flex flex-col items-center justify-center text-gray-400 hover:border-black hover:text-black transition-colors"
                  data-testid="trade-library-cover-empty"
                >
                  <ImageIcon className="w-6 h-6 mb-2" strokeWidth={1.5} />
                  <p className="text-xs">{uploadingCover ? 'Uploading…' : 'Choose Image'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">JPG / PNG / WebP</p>
                </button>
              )}
            </div>

            <div className="md:col-span-8 space-y-4 max-w-2xl">
              <div className="flex items-center gap-3 border border-gray-100 p-3 bg-white rounded" data-testid="trade-library-file-slot">
                <div className="w-10 h-10 bg-black text-white flex items-center justify-center shrink-0 rounded">
                  <FileText className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  {assetFile ? (
                    <p className="text-xs font-medium text-gray-800 truncate" data-testid="trade-library-file-name">{assetFile.filename || 'Current file'}</p>
                  ) : (
                    <p className="text-xs text-gray-400">No file selected</p>
                  )}
                  <p className="text-[10px] text-gray-400">Max 100 MB · any file type</p>
                </div>
                <Button variant="outline" size="sm" className="text-xs bg-white text-gray-900" onClick={() => fileRef.current?.click()} disabled={uploadingFile} data-testid="trade-library-file-choose">
                  <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                  {uploadingFile ? 'Uploading…' : (assetFile ? 'Replace' : 'Choose file')}
                </Button>
              </div>

              <LocalizedField label="Title *" testid="trade-library-title" value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} placeholder="e.g. AIR LINE Technical Specifications 2026" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase font-semibold text-gray-400 mb-1.5">Category *</label>
                  <select
                    value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-gray-200 text-xs rounded appearance-none cursor-pointer font-medium bg-white focus:outline-none focus:border-black"
                    style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='1.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center' }}
                    data-testid="trade-library-category"
                  >
                    <option value="catalogue">Catalogue</option>
                    <option value="3d_model">3D Model</option>
                    <option value="tech_pack">Technical Pack</option>
                    <option value="brochure">Brochure</option>
                    <option value="specification">Specification</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase font-semibold text-gray-400 mb-1.5">File Language *</label>
                  <select
                    value={form.language || 'en'} onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-gray-200 text-xs rounded appearance-none cursor-pointer font-medium bg-white focus:outline-none focus:border-black"
                    style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='1.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center' }}
                    data-testid="trade-library-language"
                  >
                    <option value="en">English (EN)</option>
                    <option value="ro">Romanian (RO)</option>
                    <option value="nl">Dutch (NL)</option>
                    <option value="ru">Russian (RU)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase font-semibold text-gray-400 mb-1.5">Sub-tag (optional)</label>
                  <input
                    type="text" value={form.sub_tag} onChange={(e) => setForm((p) => ({ ...p, sub_tag: e.target.value }))}
                    placeholder="e.g. AIR LINE, RADIAL"
                    className="w-full px-2.5 py-1.5 border border-gray-200 text-xs rounded focus:outline-none focus:border-black"
                    data-testid="trade-library-subtag"
                  />
                </div>
              </div>

              <LocalizedField label="Description (optional)" testid="trade-library-description" multiline rows={2} value={form.description} onChange={(v) => setForm((p) => ({ ...p, description: v }))} />

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" className="text-xs bg-white text-gray-900" onClick={cancelForm} data-testid="trade-library-cancel-btn">
                  {t?.common?.cancel || 'Cancel'}
                </Button>
                <button 
                  type="button"
                  onClick={handleSave} 
                  disabled={busy || uploadingFile} 
                  className="inline-flex items-center justify-center bg-black !text-white text-xs px-4 h-9 font-medium transition-colors rounded-none" 
                  data-testid="trade-library-save-btn"
                >
                  {busy ? 'Saving…' : (editingId ? 'Save Changes' : 'Publish Asset')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {loading ? (
        <p className="text-gray-400 text-xs py-6">{t?.common?.loading || 'Loading…'}</p>
      ) : assets.length === 0 ? (
        <div className="border border-dashed border-gray-200 p-10 text-center rounded bg-gray-50" data-testid="trade-library-empty">
          <Library className="w-8 h-8 mx-auto mb-2 text-gray-400" strokeWidth={1.5} />
          <p className="text-xs text-gray-400">No trade assets uploaded yet.</p>
        </div>
      ) : (
        <div className="border border-gray-100 rounded divide-y divide-gray-100 bg-gray-50" data-testid="trade-library-list">
          {assets.map((a) => {
            const titleStr = getLocalized(a.title, 'en');
            return (
              <div key={a.id} className="p-3.5 flex items-center gap-4 border-b border-gray-100 last:border-b-0" data-testid={`trade-library-item-${a.id}`}>
                {a.image_url ? (
                  <img src={a.image_url} alt="" className="w-12 h-12 object-cover border border-gray-200 rounded shrink-0" data-testid={`trade-library-thumb-${a.id}`} />
                ) : (
                  <div className="w-12 h-12 bg-black text-white flex items-center justify-center shrink-0 rounded">
                    <FileText className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{titleStr}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span>{ASSET_CATEGORIES.find((c) => c.value === a.category)?.label || a.category}</span>
                    <span className="px-1.5 py-0.5 bg-neutral-900 text-white font-mono uppercase text-[8.5px] tracking-wider font-semibold rounded-sm shrink-0">
                      {a.language || 'en'}
                    </span>
                    {a.sub_tag && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded font-semibold tracking-wider uppercase text-[9px]">{a.sub_tag}</span>}
                    {a.filename && <span className="text-gray-300 truncate">· {a.filename}</span>}
                  </p>
                </div>
                <a href={a.file_url} target="_blank" rel="noopener noreferrer" className="text-[11px] font-medium text-gray-400 hover:text-black border-b border-transparent hover:border-black pb-0.5">
                  Preview
                </a>
                <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`trade-library-edit-${a.id}`} className="bg-white text-gray-900">
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(a.id, titleStr)} className="text-destructive bg-white" data-testid={`trade-library-delete-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ============================================================
// B2B Partners CRM Management Section
// ============================================================
const B2BPartnersSection = ({ token }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPartner, setEditingPartner] = useState(null);
  const [discountInput, setDiscountInput] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPartners = async () => {
    setLoading(true);
    const routes = ['/api/admin/users', '/api/admin/partners', '/api/admin/b2b/partners'];
    let isResolved = false;

    for (const route of routes) {
      try {
        const res = await fetch(`${API}${route}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          let allUsers = Array.isArray(data) ? data : (data.users || data.partners || data.data || Object.values(data).find(Array.isArray) || []);
          const filteredPartners = allUsers.filter(u => u && u.role !== 'admin');
          setPartners(filteredPartners);
          isResolved = true;
          break;
        }
      } catch (err) {
        console.error(err);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token) fetchPartners();
  }, [token]);

  const handleToggleApprove = async (partner) => {
    const nextStatus = !partner.is_approved;
    try {
      const res = await fetch(`${API}/api/admin/users/${partner.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_approved: nextStatus })
      });
      if (!res.ok) throw new Error();
      toast.success(nextStatus ? 'Partner approved' : 'Partner verification revoked');
      setPartners(prev => prev.map(p => p.id === partner.id ? { ...p, is_approved: nextStatus } : p));
    } catch {
      toast.error('Failed to update partner verification status');
    }
  };

  const openDiscountEdit = (partner) => {
    setEditingPartner(partner);
    setDiscountInput(partner.trade_discount !== null && partner.trade_discount !== undefined ? Math.round(partner.trade_discount * 100).toString() : '');
  };

  const handleSaveDiscount = async () => {
    if (!editingPartner) return;
    const val = parseFloat(discountInput);
    if (!isNaN(val) && (val < 0 || val > 100)) {
      toast.error('Discount must be between 0% and 100%');
      return;
    }
    
    setSaving(true);
    try {
      const trade_discount = isNaN(val) ? null : val / 100;
      const res = await fetch(`${API}/api/admin/users/${editingPartner.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ trade_discount })
      });
      if (!res.ok) throw new Error();
      toast.success(`Discount updated for ${editingPartner.company_name || editingPartner.email}`);
      setPartners(prev => prev.map(p => p.id === editingPartner.id ? { ...p, trade_discount } : p));
      setEditingPartner(null);
    } catch {
      toast.error('Failed to update custom trade discount');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-border p-8 bg-white text-left" data-testid="b2b-partners-crm">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <p className="text-caption text-muted-foreground mb-2 flex items-center gap-2 text-xs">
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> CRM Control
          </p>
          <h2 className="text-xl font-medium text-gray-900">B2B Partners Management</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Verify trade accounts, view partner details, and specify custom professional discounts.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPartners} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh List
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-xs py-6">Loading B2B accounts…</p>
      ) : partners.length === 0 ? (
        <div className="border border-dashed border-gray-200 p-10 text-center bg-gray-50">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" strokeWidth={1.5} />
          <p className="text-xs text-gray-400">No B2B accounts registered on the platform.</p>
        </div>
      ) : (
        <div className="border border-border overflow-x-auto rounded-none shadow-sm bg-white">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 border-b border-border">
              <tr className="text-left text-xs tracking-wider text-gray-400 uppercase font-semibold">
                <th className="px-5 py-4 font-medium">Company / Name</th>
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">Trade Role</th>
                <th className="px-5 py-4 font-medium text-center">Status</th>
                <th className="px-5 py-4 font-medium text-center">Custom Discount</th>
                <th className="px-5 py-4 font-medium w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border font-light">
              {partners.map((partner) => (
                <tr key={partner.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900 text-sm">{partner.company_name || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{[partner.first_name, partner.last_name].filter(Boolean).join(' ') || 'Unnamed Partner'}</p>
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-600">
                    <p className="font-mono">{partner.email}</p>
                    {partner.phone && <p className="mt-0.5 font-mono text-gray-400">{partner.phone}</p>}
                  </td>
                  <td className="px-5 py-4 capitalize text-xs font-medium text-gray-700">
                    {partner.preferred_language ? (
                      <span className="mr-2 px-1.5 py-0.5 bg-gray-100 font-mono uppercase text-[9px] text-gray-400 rounded-sm">
                        {partner.preferred_language}
                      </span>
                    ) : null}
                    {(partner.role || 'Partner').replace('_', ' ')}
                  </td>
                  <td className="px-5 py-4 text-center whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleToggleApprove(partner)}
                      className={`inline-flex items-center gap-1 px-3 py-1 border text-[10px] tracking-wider uppercase font-medium transition-colors ${
                        partner.is_approved
                          ? 'border-emerald-500/30 text-emerald-600 bg-emerald-500/5 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                          : 'border-amber-500/40 text-amber-600 bg-amber-500/5 hover:bg-emerald-600 hover:text-white hover:border-emerald-600'
                      }`}
                    >
                      {partner.is_approved ? '✓ Verified' : '⏳ Approve'}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-center">
                    {partner.trade_discount !== null && partner.trade_discount !== undefined ? (
                      <span className="font-mono text-gray-900 font-semibold text-sm bg-neutral-100 px-2 py-1 border border-neutral-200">
                        {Math.round(partner.trade_discount * 100)}%
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Default role rate</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDiscountEdit(partner)}
                      className="text-xs bg-white text-gray-900 h-8 rounded-none px-3"
                    >
                      <Percent className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Rate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL: Edit Trade Discount */}
      <AnimatePresence>
        {editingPartner && (
          <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 15 }} className="bg-white border border-border rounded-none shadow-xl w-full max-w-md p-6">
              <div className="flex items-start justify-between border-b border-gray-100 pb-4 mb-4">
                <div>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-[#9E9E9E] font-medium">Discount settings</p>
                  <h3 className="text-lg font-light text-gray-900 truncate max-w-[300px]">{editingPartner.company_name || editingPartner.email}</h3>
                </div>
                <button onClick={() => setEditingPartner(null)} className="text-gray-400 hover:text-black"><X className="w-5 h-5" strokeWidth={1.5} /></button>
              </div>
              <div className="space-y-4">
                <label className="block">
                  <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 block mb-2">Individual Professional Discount (%)</span>
                  <div className="relative">
                    <input type="number" min="0" max="100" placeholder="e.g. 35" value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} className="w-full px-3 py-2 border border-[#ECECEC] bg-white text-sm focus:outline-none focus:border-[#0A0A0A] rounded-none h-11 pr-10 font-mono text-gray-900" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-gray-400 text-sm">%</span>
                  </div>
                </label>
                <p className="text-[11px] text-[#9E9E9E] font-light leading-relaxed">Leave this input empty to clear custom rates and restore the default system discount for this partner's trade role.</p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-5 mt-6 border-t border-gray-100">
                <Button variant="outline" size="sm" className="rounded-none h-10 px-4 text-xs" onClick={() => setEditingPartner(null)} disabled={saving}>Cancel</Button>
                <button type="button" onClick={handleSaveDiscount} disabled={saving} className="inline-flex items-center justify-center bg-black text-white text-xs px-5 h-10 font-medium transition-colors rounded-none shadow-sm">
                  {saving ? 'Saving…' : 'Save Rate'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ============================================================
// B2B & B2C ACTIVE PIPELINE ENGINE
// ============================================================
const B2BOrdersSection = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    reference: '', project_name: '', client_type: 'b2c', channel: 'online',
    is_registered: true, user_id: '', client_name: '', email: '', phone: '',
    country: '', city: '', address: '', services: [],
    launch_date: getTodayDateString()
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const resOrders = await fetch(`${API}/api/admin/b2b/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resOrders.ok) {
        const data = await resOrders.json();
        setOrders(data.orders || []);
      }

      const routes = ['/api/admin/users', '/api/admin/partners', '/api/admin/b2b/partners'];
      for (const route of routes) {
        try {
          const res = await fetch(`${API}${route}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            let allUsers = Array.isArray(data) ? data : (data.users || data.partners || data.data || Object.values(data).find(Array.isArray) || []);
            setPartners(allUsers);
            break;
          }
        } catch (e) {
          console.error(e);
        }
      }
    } catch (err) {
      toast.error('Failed to load active pipeline records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const handleStatusChange = async (orderId, nextStatus) => {
    try {
      const res = await fetch(`${API}/api/admin/b2b/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: nextStatus })
      });
      if (res.ok) {
        if (nextStatus === 'completed') {
          toast.success('Project completed and moved to the Analytics dashboard module.');
        } else {
          toast.success(`Lifecycle advanced to: ${STAGE_LABELS[nextStatus]}`);
        }
        fetchData();
      }
    } catch {
      toast.error('Failed to update production stage');
    }
  };

  const toggleService = (srv) => {
    setForm(p => ({
      ...p,
      services: p.services.includes(srv) ? p.services.filter(s => s !== srv) : [...p.services, srv]
    }));
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!form.project_name.trim()) { toast.error('Project Specification is required'); return; }
    if (!form.country.trim()) { toast.error('Destination Country input is required'); return; }
    if (form.is_registered && !form.user_id) { toast.error('Please bind an authenticated client profile'); return; }

    setSubmitting(true);
    let finalPayload = { ...form };
    
    if (form.is_registered) {
      const selectedUser = partners.find(p => p.id === form.user_id);
      if (selectedUser) {
        finalPayload.client_name = form.client_name.trim() || `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || selectedUser.company_name || 'STUDIO';
        finalPayload.email = form.email.trim() || selectedUser.email;
        finalPayload.phone = form.phone.trim() || selectedUser.phone || '';
      }
    }

    try {
      const res = await fetch(`${API}/api/admin/b2b/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(finalPayload)
      });
      if (res.ok) {
        toast.success('Active factory track initialized successfully!');
        setShowCreateModal(false);
        setForm({
          reference: '', project_name: '', client_type: 'b2c', channel: 'online',
          is_registered: true, user_id: '', client_name: '', email: '', phone: '',
          country: '', city: '', address: '', services: [],
          launch_date: getTodayDateString()
        });
        fetchData();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Initialization contract aborted');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOrder = async (id, ref) => {
    if (!window.confirm(`Permanently remove track ${ref} from system database?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/b2b/orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Order track purged');
        fetchData();
      }
    } catch {
      toast.error('Failed to remove track');
    }
  };

  return (
    <div className="space-y-6 text-left font-light">
      <div className="flex items-start justify-between gap-6 mb-4 flex-wrap">
        <div>
          <p className="text-caption text-muted-foreground mb-1 flex items-center gap-2 text-xs">
            <Package className="w-3.5 h-3.5" strokeWidth={1.5} /> Active Pipeline
          </p>
          <h2 className="text-xl font-medium text-gray-900">Factory Assembly Tracking</h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
            Monitor and advance active factory floor operations. Completed steps auto-archive instantly.
          </p>
        </div>
        
        {/* ИСПРАВЛЕНО: Кнопка «Initialize Project Track» и кнопка «Refresh» приведены к единому стандарту h-10 (40px) */}
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowCreateModal(true)} 
            className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium"
            style={{ height: '40px' }}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Initialize Project Track
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchData} 
            disabled={loading} 
            className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none p-0 shadow-sm hover:bg-neutral-50 flex items-center justify-center transition-colors w-11"
            style={{ height: '40px' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400 text-xs py-6">Loading pipeline layout structures…</p>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-gray-200 p-10 text-center bg-gray-50">
          <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" strokeWidth={1.5} />
          <p className="text-xs text-gray-400">No active configurations inside the factory floor loop.</p>
        </div>
      ) : (
        <div className="border border-border overflow-x-auto rounded-none shadow-sm bg-white">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 border-b border-border">
              <tr className="text-left text-xs tracking-wider text-gray-400 uppercase font-semibold">
                <th className="px-5 py-4 font-medium w-36">Reference</th>
                <th className="px-5 py-4 font-medium">Project Track Details</th>
                <th className="px-5 py-4 font-medium">Client / Destination</th>
                <th className="px-5 py-4 font-medium">Segment & Channel</th>
                <th className="px-5 py-4 font-medium text-center w-64">Current Factory Stage</th>
                <th className="px-5 py-4 font-medium w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border font-light text-gray-700">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-5 py-4 font-mono font-bold text-xs text-gray-900">{order.reference}</td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900 text-sm">{order.project_name}</p>
                    {order.services && order.services.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {order.services.map(s => <span key={s} className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded text-[9px] uppercase font-bold">{s === 'measurement' ? 'Замер' : s === 'delivery' ? 'Доставка' : 'Монтаж'}</span>)}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs">
                    <p className="font-medium text-neutral-800">{order.client_name || '—'}</p>
                    <p className="text-gray-400 text-[10px] mt-0.5">{order.country}, {order.city || '—'}</p>
                  </td>
                  <td className="px-5 py-4 text-xs font-medium">
                    <span className={`px-2 py-0.5 rounded font-mono text-[9px] uppercase font-bold mr-1.5 ${order.client_type === 'b2b' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-purple-50 text-purple-600 border border-purple-200'}`}>{order.client_type}</span>
                    <span className={`px-2 py-0.5 rounded font-mono text-[9px] uppercase font-bold ${order.channel === 'online' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{order.channel}</span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      className={`w-full h-10 px-3 border text-xs font-semibold cursor-pointer focus:outline-none focus:border-black rounded-none transition-all ${getStageStyle(order.status)}`}
                      style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='1.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', appearance: 'none' }}
                    >
                      {STAGES_ORDER.map(stage => (
                        <option key={stage} value={stage} className="bg-white text-gray-900 font-medium">{STAGE_LABELS[stage]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button type="button" onClick={() => handleDeleteOrder(order.id, order.reference)} className="text-gray-400 hover:text-red-600 transition-colors">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* INITIALIZE TRACK MODAL */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 overflow-y-auto">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 15 }} className="bg-white border border-border rounded-none shadow-xl w-full max-w-lg p-6 my-8">
              <div className="flex items-start justify-between border-b border-gray-100 pb-4 mb-4">
                <div>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-[#9E9E9E] font-medium">Pipeline Setup</p>
                  <h3 className="text-lg font-light text-gray-900">Initialize Production Order</h3>
                </div>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-black"><X className="w-5 h-5" strokeWidth={1.5} /></button>
              </div>
              <form onSubmit={handleCreateOrder} className="space-y-4 text-xs text-left">
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1">Invoice / Ref ID (optional)</label>
                    <input type="text" placeholder="Auto-generated if blank" value={form.reference} onChange={(e) => setForm(p => ({ ...p, reference: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] bg-white focus:outline-none focus:border-black font-mono text-gray-900 rounded-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1">Project Specification *</label>
                    <input type="text" required placeholder="e.g. Glass Doors System" value={form.project_name} onChange={(e) => setForm(p => ({ ...p, project_name: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] bg-white focus:outline-none focus:border-black text-gray-900 rounded-none" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1">Launch Date / Дата запуска *</label>
                    <input 
                      type="date" 
                      required 
                      value={form.launch_date} 
                      onChange={(e) => setForm(p => ({ ...p, launch_date: e.target.value }))} 
                      className="w-full px-3 h-10 border border-[#ECECEC] bg-white focus:outline-none focus:border-black text-gray-900 rounded-none" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-3 border border-neutral-100">
                  <div>
                    <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1">Client Classification</label>
                    <select value={form.client_type} onChange={(e) => setForm(p => ({ ...p, client_type: e.target.value }))} className="w-full h-10 px-2 border border-[#ECECEC] bg-white focus:outline-none focus:border-black font-medium text-gray-900 rounded-none">
                      <option value="b2c">B2C Retail Buyer</option>
                      <option value="b2b">B2B Trade Partner</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1">Sales Channel</label>
                    <select value={form.channel} onChange={(e) => setForm(p => ({ ...p, channel: e.target.value }))} className="w-full h-10 px-2 border border-[#ECECEC] bg-white focus:outline-none focus:border-black font-medium text-gray-900 rounded-none">
                      <option value="online">Online Platform framework</option>
                      <option value="offline">Offline Direct Office / Showroom</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2 py-1">
                  <input type="checkbox" id="is_reg" checked={form.is_registered} onChange={(e) => setForm(p => ({ ...p, is_registered: e.target.checked }))} className="cursor-pointer" />
                  <label htmlFor="is_reg" className="text-gray-600 select-none cursor-pointer font-medium">Link with an authenticated website profile</label>
                </div>

                {form.is_registered && (
                  <div>
                    <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5">Select Web Profile *</label>
                    <select value={form.user_id} onChange={(e) => setForm(p => ({ ...p, user_id: e.target.value }))} className="w-full h-10 px-2 border border-[#ECECEC] bg-white focus:outline-none focus:border-black font-medium text-gray-900 rounded-none">
                      <option value="">-- Choose Account from Database --</option>
                      {partners.map(p => (
                        <option key={p.id} value={p.id}>{p.company_name || p.email} ({p.first_name} {p.last_name})</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-3 p-3 bg-gray-50 border border-gray-100 rounded text-gray-900">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Shipping destination details</p>
                  
                  {!form.is_registered && (
                    <div className="grid grid-cols-3 gap-3">
                      <input type="text" placeholder="Client Name" value={form.client_name} onChange={(e) => setForm(p => ({ ...p, client_name: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-xs focus:outline-none focus:border-black text-gray-900 rounded-none" />
                      <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-xs focus:outline-none focus:border-black text-gray-900 rounded-none" />
                      <input type="text" placeholder="Phone Index" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-xs focus:outline-none focus:border-black text-gray-900 rounded-none" />
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <input type="text" required placeholder="Country (e.g. Ireland, France) *" value={form.country} onChange={(e) => setForm(p => ({ ...p, country: e.target.value }))} className="w-full px-2.5 h-9 border border-neutral-300 font-bold bg-white text-xs focus:outline-none focus:border-black text-gray-900 rounded-none" />
                    <input type="text" placeholder="City" value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-xs focus:outline-none focus:border-black text-gray-900 rounded-none" />
                    <input type="text" placeholder="Street, building, suite" value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-xs focus:outline-none focus:border-black text-gray-900 rounded-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2">Ancillary Services</label>
                  <div className="flex gap-4 flex-wrap text-gray-900">
                    {['measurement', 'delivery', 'installation'].map((srv) => (
                      <label key={srv} className="flex items-center gap-1.5 select-none cursor-pointer text-gray-700 font-medium">
                        <input type="checkbox" checked={form.services.includes(srv)} onChange={() => toggleService(srv)} />
                        {srv === 'measurement' ? 'Замер' : srv === 'delivery' ? 'Доставка' : 'Монтаж'}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100 mt-6">
                  <Button type="button" variant="outline" size="sm" className="rounded-none h-10 px-4 text-xs bg-white text-gray-900 border-gray-200" onClick={() => setShowCreateModal(false)} disabled={submitting}>Cancel</Button>
                  <button type="submit" disabled={submitting} className="inline-flex items-center justify-center bg-black text-white text-xs px-5 h-10 font-medium transition-colors rounded-none shadow-sm">
                    {submitting ? 'Processing...' : 'Launch Factory Pipeline'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// MAIN PAGE CORE CONTAINER
// ============================================================
export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  
  const [hydrated, setHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('b2b_system');
  
  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/settings' } });
    }
  }, [hydrated, navigate]);

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-xs text-gray-400 bg-background">Loading configurations…</div>;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-background text-left font-light">
      <SEO title="Settings · Admin" description="Global layout configuration engine" path="admin/settings" />
      <AdminHeader active="settings" />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
        <div className="mb-12">
          <p className="text-caption text-muted-foreground mb-3 text-xs tracking-wider uppercase text-gray-400 font-semibold">CMS & Operations Core</p>
          <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-gray-900">Settings</h1>
          <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed text-gray-500">
            Configure dynamic interface texts, global asset libraries, B2B partner authorizations, and factory floor tracker pipelines.
          </p>
        </div>

        <div className="flex gap-6 border-b border-border pb-px mb-8 overflow-x-auto whitespace-nowrap scrollbar-none">
          <button
            onClick={() => setActiveTab('b2b_system')}
            className={`flex items-center gap-1.5 pb-3 text-xs tracking-wider uppercase font-medium border-b transition-all ${
              activeTab === 'b2b_system' ? 'border-foreground text-foreground font-semibold border-slate-900' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sliders className="w-4 h-4" /> Trade Library (B2B)
          </button>

          <button
            onClick={() => setActiveTab('b2b_partners')}
            className={`flex items-center gap-1.5 pb-3 text-xs tracking-wider uppercase font-medium border-b transition-all ${
              activeTab === 'b2b_partners' ? 'border-foreground text-foreground font-semibold border-slate-900' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="w-4 h-4" /> Partners Directory
          </button>

          <button
            onClick={() => setActiveTab('b2b_orders')}
            className={`flex items-center gap-1.5 pb-3 text-xs tracking-wider uppercase font-medium border-b transition-all ${
              activeTab === 'b2b_orders' ? 'border-foreground text-foreground font-semibold border-slate-900' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Package className="w-4 h-4" /> Production Pipeline
          </button>
        </div>

        <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm">
          {activeTab === 'b2b_system' && (
            <div className="space-y-6 animate-fadeIn">
              <CatalogueSection token={token} />
              <TradeLibrarySection token={token} />
            </div>
          )}

          {activeTab === 'b2b_partners' && (
            <div className="space-y-6 animate-fadeIn">
              <B2BPartnersSection token={token} />
            </div>
          )}

          {activeTab === 'b2b_orders' && (
            <div className="space-y-6 animate-fadeIn">
              <B2BOrdersSection token={token} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
